import React, { useState, useMemo, useRef } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Grid,
  Divider,
  Tooltip,
  InputAdornment,
} from "@mui/material";
import {
  Close as CloseIcon,
  Download,
  PictureAsPdf,
  Search,
  PersonAdd,
  FilterAlt,
} from "@mui/icons-material";
import ExportButtons from "../components/btns/ExportButtons";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import enGB from "date-fns/locale/en-GB";
import * as XLSX from "xlsx";
import PageHeader from "../components/headers/Header";

// --- EXPORT LIBRARIES ---
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// --- ASSETS ---
import marabesLogo from "../assets/images/logos/Marabes.png";

// Mock API call - replace with your actual service
import { getAttendanceReport } from "../services/reportingAPIs";

// === TIME CONVERSION UTILITIES (EXACT VALUES - NO MAJORATION) ===
/**
 * Converts decimal hours to exact minutes (rounded to nearest minute)
 * @param {number} hours - Decimal hours (e.g., 0.283 = 17 minutes)
 * @returns {number} Exact minutes rounded to nearest whole minute
 */
const hoursToMinutes = (hours) => {
  if (hours == null || hours === 0) return 0;
  return Math.round(hours * 60); // Exact conversion: 0.283h = 17m, 0.533h = 32m
};

/**
 * Converts hours to days (8-hour workday) with 1 decimal precision
 * @param {number} hours
 * @returns {number} Days with 1 decimal place (e.g., 4 hours = 0.5 days)
 */
const hoursToDays = (hours) => {
  if (hours == null || hours === 0) return 0;
  return Math.round((hours / 8) * 10) / 10; // 1 decimal place: 4h → 0.5, 12h → 1.5
};

const EmployeesAttendance = () => {
  const componentRef = useRef();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [attendanceData, setAttendanceData] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    return {
      from: startOfMonth,
      to: today,
    };
  });
  const [exportingPDF, setExportingPDF] = useState(false);

  // Fetch attendance data
  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const formatDateForBackend = (date) => {
        if (!date) return null;
        return new Date(date).toLocaleDateString("en-CA");
      };

      const from = formatDateForBackend(dateRange.from);
      const to = formatDateForBackend(dateRange.to);

      const result = await getAttendanceReport(from, to);

      const getPositions = (emp) => {
        if (Array.isArray(emp.positions) && emp.positions.length > 0) return emp.positions.join(', ');
        if (Array.isArray(emp.position) && emp.position.length > 0) return emp.position.join(', ');
        return emp.position || "Unknown";
      };

      // Extract unique employees for selector
      const employees = [
        ...new Set(
          result.map((emp) => ({
            id: emp.employeeId,
            name: emp.employeeName || "N/A",
            department: emp.department || "Unknown",
            position: getPositions(emp),
          })),
        ),
      ].sort((a, b) => a.name.localeCompare(b.name));

      setAttendanceData(result || []);
      setAllEmployees(employees);
      return employees;
    } catch (err) {
      setError(err.message || "Failed to load attendance data");
      setAttendanceData([]);
      setAllEmployees([]);
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Filter employees for selector
  const filteredEmployees = useMemo(() => {
    if (!employeeSearch.trim()) return allEmployees;
    const searchTerm = employeeSearch.toLowerCase();
    return allEmployees.filter(
      (emp) =>
        emp.name.toLowerCase().includes(searchTerm) ||
        emp.department.toLowerCase().includes(searchTerm) ||
        emp.position.toLowerCase().includes(searchTerm),
    );
  }, [allEmployees, employeeSearch]);

  // Filter table data by selected employees
  const filteredTableData = useMemo(() => {
    if (selectedEmployees.length === 0) return [];
    return attendanceData.filter((emp) =>
      selectedEmployees.some((selected) => selected.id === emp.employeeId),
    );
  }, [attendanceData, selectedEmployees]);

  // Get base64 image from URL
  const getBase64FromUrl = async (url) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.setAttribute("crossOrigin", "anonymous");
      img.src = url;
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      };
      img.onerror = () => {
        console.warn("Failed to load image:", url);
        resolve(null);
      };
    });
  };

  // PDF Export with Logos and Header - PORTRAIT (FULL WIDTH TABLE)
  const handleExportPDF = async () => {
    if (filteredTableData.length === 0) return;

    setExportingPDF(true);
    try {
      const doc = new jsPDF("p", "mm", "a4");
      const pageMargin = 10;
      const pageWidth = doc.internal.pageSize.width;
      const axoLogoUrl = "/cLogo.png";

      // Get logo base64 data
      const [marabesBase64, axoBase64] = await Promise.all([
        getBase64FromUrl(marabesLogo),
        getBase64FromUrl(axoLogoUrl),
      ]);

      // Add header with background
      const addHeader = (pageNum) => {
        if (pageNum > 1) doc.addPage();

        doc.setFillColor(243, 244, 246);
        doc.rect(0, 0, pageWidth, 35, "F");

        if (marabesBase64) {
          doc.addImage(marabesBase64, "PNG", pageMargin, 10, 25, 8);
        }

        if (axoBase64) {
          doc.addImage(
            axoBase64,
            "PNG",
            pageWidth - pageMargin - 25,
            10,
            25,
            8,
          );
        }

        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(30, 41, 59);
        doc.text("Employee Attendance Report", pageWidth / 2, 16, {
          align: "center",
        });

        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(100, 116, 139);

        const dateRangeText =
          dateRange.from && dateRange.to
            ? `${new Date(dateRange.from).toLocaleDateString("en-GB")} to ${new Date(dateRange.to).toLocaleDateString("en-GB")}`
            : "All Time";

        doc.text(`Report Period: ${dateRangeText}`, pageWidth / 2, 22, {
          align: "center",
        });
        doc.text(
          `Generated: ${new Date().toLocaleDateString("en-GB")}`,
          pageWidth / 2,
          27,
          { align: "center" },
        );
      };

      addHeader(1);

      // Prepare table data with EXACT conversions (lateness in mins, personal in hours)
      const tableRows = filteredTableData.map((emp) => {
        const latenessMins = hoursToMinutes(emp.totalLatenessHours);
        const personalHours = emp.totalPersonalHours || 0; // Personal in HOURS (1 decimal)
        const sicknessDays = hoursToDays(emp.totalSicknessHours);

        return [
          emp.employeeName || "N/A",
          emp.department || "Unknown",
          emp.leaveDays?.ANNUAL_LEAVE !== undefined
            ? emp.leaveDays.ANNUAL_LEAVE
            : 0,
          emp.leaveDays?.UNPAID_LEAVE !== undefined
            ? emp.leaveDays.UNPAID_LEAVE
            : 0,
          emp.totalSicknessRecords || 0,
          sicknessDays.toFixed(1), // 1 decimal place
          emp.totalLatenessRecords || 0,
          latenessMins, // LATENESS in EXACT minutes
          emp.totalPersonalRecords || 0,
          Number(personalHours).toFixed(1), // PERSONAL in HOURS (1 decimal)
        ];
      });

      autoTable(doc, {
        startY: 40,
        margin: { left: pageMargin, right: pageMargin },
        head: [
          [
            "Employee Name",
            "Department",
            "Annual Leave\nVacation Taken",
            "Annual Leave\nUnpaid Vacation",
            "Sickness\nRec",
            "Sickness\nDays",
            "Lateness\nRec",
            "Lateness\nMins",
            "Personal\nRec",
            "Personal\nHours",
          ],
        ],
        body: tableRows,
        theme: "grid",
        styles: {
          fontSize: 7,
          cellPadding: 2,
          halign: "center",
          overflow: "linebreak",
        },
        headStyles: {
          fillColor: [30, 41, 59],
          textColor: [255, 255, 255],
          fontSize: 7,
          fontStyle: "bold",
          minCellHeight: 10,
        },
        columnStyles: {
          0: { halign: "left", fontStyle: "bold", minCellWidth: 28 },
          1: { halign: "left", minCellWidth: 22 },
          2: { minCellWidth: 14 },
          3: { minCellWidth: 14 },
          4: { minCellWidth: 10 },
          5: { minCellWidth: 10 },
          6: { minCellWidth: 10 },
          7: { minCellWidth: 12 },
          8: { minCellWidth: 10 },
          9: { minCellWidth: 12 },
        },
        willDrawPage: (data) => {
          if (data.pageNumber > 1) addHeader(data.pageNumber);
        },
        didDrawPage: (data) => {
          doc.setFontSize(8);
          doc.text(
            `Page ${data.pageNumber}`,
            pageWidth - pageMargin,
            doc.internal.pageSize.height - 10,
            { align: "right" },
          );
        },
      });

      const fileName = `Employee_Attendance_${dateRange.from ? new Date(dateRange.from).toLocaleDateString("en-GB").replace(/\//g, "-") : "Start"}_to_${dateRange.to ? new Date(dateRange.to).toLocaleDateString("en-GB").replace(/\//g, "-") : "End"}.pdf`;
      doc.save(fileName);
    } catch (err) {
      console.error("PDF export failed:", err);
      alert("Failed to export PDF. Please try again.");
    } finally {
      setExportingPDF(false);
    }
  };

  // FIXED EXCEL EXPORT - Lateness in Mins, Personal in Hours (no majoration)
  const handleExportExcel = () => {
    if (filteredTableData.length === 0) {
      alert("No employee data to export.");
      return;
    }

    try {
      // Prepare data with exact conversions (lateness in mins, personal in hours)
      const exportData = filteredTableData.map((emp) => {
        const latenessMins = hoursToMinutes(emp.totalLatenessHours);
        const personalHours = emp.totalPersonalHours || 0; // Personal in HOURS
        const sicknessDays = hoursToDays(emp.totalSicknessHours);

        return {
          "Employee Name": emp.employeeName || "N/A",
          Department: emp.department || "N/A",
          Position: Array.isArray(emp.positions) ? emp.positions.join(', ') : (Array.isArray(emp.position) ? emp.position.join(', ') : (emp.position || "N/A")),
          "Vacation Taken":
            emp.leaveDays?.ANNUAL_LEAVE !== undefined
              ? emp.leaveDays.ANNUAL_LEAVE
              : 0,
          "Unpaid Vacation":
            emp.leaveDays?.UNPAID_LEAVE !== undefined
              ? emp.leaveDays.UNPAID_LEAVE
              : 0,
          "Sickness Records": emp.totalSicknessRecords || 0,
          "Sickness Days": sicknessDays, // Exact days with 1 decimal
          "Lateness Records": emp.totalLatenessRecords || 0,
          "Lateness Minutes": latenessMins, // LATENESS in EXACT minutes
          "Personal Records": emp.totalPersonalRecords || 0,
          "Personal Hours": personalHours, // PERSONAL in HOURS (decimal)
        };
      });

      // Create worksheet
      const ws = XLSX.utils.json_to_sheet(exportData);

      // Set column widths
      ws["!cols"] = [
        { wch: 25 }, // Employee Name
        { wch: 20 }, // Department
        { wch: 20 }, // Position
        { wch: 15 }, // Vacation Taken
        { wch: 15 }, // Unpaid Vacation
        { wch: 15 }, // Sickness Records
        { wch: 15 }, // Sickness Days
        { wch: 15 }, // Lateness Records
        { wch: 16 }, // Lateness Minutes
        { wch: 15 }, // Personal Records
        { wch: 15 }, // Personal Hours
      ];

      // Create workbook
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Attendance Report");

      // Generate filename
      const fileName = `Employee_Attendance_${dateRange.from ? new Date(dateRange.from).toLocaleDateString("en-GB").replace(/\//g, "-") : "Start"}_to_${dateRange.to ? new Date(dateRange.to).toLocaleDateString("en-GB").replace(/\//g, "-") : "End"}.xlsx`;

      // Export file
      XLSX.writeFile(wb, fileName);
      console.log("Excel export successful:", fileName);
    } catch (err) {
      console.error("Excel export failed:", err);
      alert("Failed to export Excel file. Please try again.");
    }
  };

  // Clear all selections
  const handleClearAll = () => {
    setSelectedEmployees([]);
    setEmployeeSearch("");
    setDateRange({ from: null, to: null });
  };

  // Fetch attendance for the selected period and auto-select all employees
  const handleShowAllEmployees = async () => {
    setSelectedEmployees([]);
    setEmployeeSearch("");
    const employees = await fetchData();
    if (employees.length > 0) {
      setSelectedEmployees(employees);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <PageHeader title="Employees Attendance" subtitle="Track and manage precise employee attendance records" />

        {/* Filters Card */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography
              variant="h6"
              fontWeight="600"
              gutterBottom
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <FilterAlt fontSize="small" />
              Attendance Filters
            </Typography>

            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="Start Date"
                  value={dateRange.from}
                  onChange={(date) =>
                    setDateRange((prev) => ({ ...prev, from: date }))
                  }
                  inputFormat="dd/MM/yyyy"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      sx: { bgcolor: "background.paper", borderRadius: 1 },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="End Date"
                  value={dateRange.to}
                  onChange={(date) =>
                    setDateRange((prev) => ({ ...prev, to: date }))
                  }
                  inputFormat="dd/MM/yyyy"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      sx: { bgcolor: "background.paper", borderRadius: 1 },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Button
                  variant="contained"
                  color="secondary"
                  fullWidth
                  onClick={handleShowAllEmployees}
                  disabled={loading}
                  startIcon={
                    loading ? <CircularProgress size={20} /> : <PersonAdd />
                  }
                >
                  {loading ? "Loading..." : "All Employees"}
                </Button>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={fetchData}
                  disabled={loading}
                  startIcon={
                    loading ? <CircularProgress size={20} /> : <Search />
                  }
                >
                  {loading ? "Loading..." : "Fetch Attendance"}
                </Button>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleClearAll}
                  disabled={
                    loading ||
                    (!dateRange.from &&
                      !dateRange.to &&
                      selectedEmployees.length === 0)
                  }
                  startIcon={<CloseIcon />}
                >
                  Clear Filters
                </Button>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            {/* Employee Multi-Selector */}
            <Box sx={{ mt: 2 }}>
              <Typography
                variant="subtitle2"
                sx={{ mb: 1, color: "text.secondary" }}
              >
                Selected Employees ({selectedEmployees.length})
              </Typography>
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  p: 1,
                  minHeight: 44,
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 0.5,
                  bgcolor: "background.default",
                }}
              >
                {selectedEmployees.length > 0 ? (
                  selectedEmployees.map((employee) => (
                    <Chip
                      key={employee.id}
                      label={`${employee.name}`}
                      size="small"
                      onDelete={() =>
                        setSelectedEmployees((prev) =>
                          prev.filter((emp) => emp.id !== employee.id),
                        )
                      }
                      sx={{
                        bgcolor: "#1976d2",
                        color: "primary.contrastText",
                        fontSize: "0.75rem",
                        height: 28,
                        "& .MuiChip-deleteIcon": {
                          color: "primary.contrastText",
                          fontSize: "1rem",
                        },
                      }}
                    />
                  ))
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    Select employees to view attendance
                  </Typography>
                )}
              </Box>

              {/* Compact Search Bar - WIDTH 400px */}
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                placeholder="Search employees..."
                value={employeeSearch}
                onChange={(e) => setEmployeeSearch(e.target.value)}
                sx={{
                  mt: 1,
                  maxWidth: 400,
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "background.paper",
                    borderRadius: 1,
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search
                        sx={{ color: "text.secondary", fontSize: "1.25rem" }}
                      />
                    </InputAdornment>
                  ),
                  endAdornment: employeeSearch && (
                    <IconButton
                      size="small"
                      onClick={() => setEmployeeSearch("")}
                      sx={{ p: 0.25 }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  ),
                }}
              />

              {/* Employee Dropdown Suggestions */}
              {employeeSearch.trim() && filteredEmployees.length > 0 && (
                <Paper
                  sx={{
                    mt: 0.5,
                    maxHeight: 180,
                    overflow: "auto",
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                    zIndex: 1000,
                    position: "absolute",
                    width: "100%",
                    maxWidth: 400,
                    boxShadow: 3,
                    borderRadius: 1,
                  }}
                >
                  {filteredEmployees
                    .filter(
                      (emp) =>
                        !selectedEmployees.some((sel) => sel.id === emp.id),
                    )
                    .slice(0, 6)
                    .map((employee) => (
                      <Box
                        key={employee.id}
                        onClick={() => {
                          setSelectedEmployees((prev) => [...prev, employee]);
                          setEmployeeSearch("");
                        }}
                        sx={{
                          p: 1,
                          cursor: "pointer",
                          "&:hover": { bgcolor: "action.hover" },
                          borderBottom: "1px solid",
                          borderColor: "divider",
                          "&:last-child": { borderBottom: "none" },
                        }}
                      >
                        <Typography
                          variant="body2"
                          fontWeight="500"
                          sx={{ fontSize: "0.875rem" }}
                        >
                          {employee.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {employee.department} • {employee.position}
                        </Typography>
                      </Box>
                    ))}
                </Paper>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Export Actions */}
        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            mb: 2,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <Typography
            variant="h6"
            fontWeight="600"
            color="primary"
            sx={{ fontSize: "1.125rem" }}
          >
            Attendance Records ({filteredTableData.length} employee
            {filteredTableData.length !== 1 ? "s" : ""})
          </Typography>

          <Box sx={{ flexGrow: 1 }} />

          <ExportButtons
            onExportPDF={handleExportPDF}
            onExportExcel={handleExportExcel}
            isExporting={exportingPDF}
            disabled={filteredTableData.length === 0 || loading}
          />
        </Box>

        {/* Attendance Table */}
        <Card ref={componentRef}>
          <CardContent>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {loading ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight={300}
              >
                <CircularProgress size={32} />
                <Typography variant="body1" sx={{ ml: 2 }}>
                  Loading attendance data...
                </Typography>
              </Box>
            ) : filteredTableData.length === 0 ? (
              <Box sx={{ py: 4, textAlign: "center" }}>
                <PersonAdd
                  sx={{ fontSize: 40, color: "text.secondary", mb: 1.5 }}
                />
                <Typography variant="subtitle1" color="text.secondary">
                  {selectedEmployees.length === 0
                    ? "Select employees to view attendance records"
                    : "No attendance records found"}
                </Typography>
                {selectedEmployees.length > 0 &&
                  dateRange.from &&
                  dateRange.to && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 0.5, display: "block" }}
                    >
                      Try adjusting the date range or selecting different
                      employees
                    </Typography>
                  )}
              </Box>
            ) : (
              <TableContainer
                component={Paper}
                sx={{ maxHeight: 600, overflow: "auto" }}
              >
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell
                        rowSpan={2}
                        sx={{
                          fontWeight: "bold",
                          backgroundColor: "#f5f5f5",
                          minWidth: 180,
                          position: "sticky",
                          left: 0,
                          zIndex: 4,
                          boxShadow: "2px 0 4px rgba(0,0,0,0.05)",
                          fontSize: "0.875rem",
                        }}
                      >
                        Employee Details
                      </TableCell>

                      {/* Annual Leave */}
                      <TableCell
                        colSpan={2}
                        align="center"
                        sx={{
                          fontWeight: "bold",
                          backgroundColor: "#bbdefb",
                          position: "sticky",
                          top: 0,
                          zIndex: 3,
                          fontSize: "0.875rem",
                        }}
                      >
                        Annual Leave
                      </TableCell>

                      {/* Sickness */}
                      <TableCell
                        colSpan={2}
                        align="center"
                        sx={{
                          fontWeight: "bold",
                          backgroundColor: "#ffe0b2",
                          position: "sticky",
                          top: 0,
                          zIndex: 3,
                          fontSize: "0.875rem",
                        }}
                      >
                        Sickness
                      </TableCell>

                      {/* Lateness */}
                      <TableCell
                        colSpan={2}
                        align="center"
                        sx={{
                          fontWeight: "bold",
                          backgroundColor: "#e1bee7",
                          position: "sticky",
                          top: 0,
                          zIndex: 3,
                          fontSize: "0.875rem",
                        }}
                      >
                        Lateness
                      </TableCell>

                      {/* Personal */}
                      <TableCell
                        colSpan={2}
                        align="center"
                        sx={{
                          fontWeight: "bold",
                          backgroundColor: "#c8e6c9",
                          position: "sticky",
                          top: 0,
                          zIndex: 3,
                          fontSize: "0.875rem",
                        }}
                      >
                        Personal
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      {/* Annual Leave */}
                      <TableCell
                        align="center"
                        sx={{
                          backgroundColor: "#e3f2fd",
                          position: "sticky",
                          top: 40,
                          zIndex: 3,
                          fontWeight: 500,
                          width: 100,
                          fontSize: "0.75rem",
                          py: 1,
                        }}
                      >
                        Vacation Taken
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          backgroundColor: "#e3f2fd",
                          position: "sticky",
                          top: 40,
                          zIndex: 3,
                          fontWeight: 500,
                          width: 100,
                          fontSize: "0.75rem",
                          py: 1,
                        }}
                      >
                        Unpaid Vacation
                      </TableCell>

                      {/* Sickness */}
                      <TableCell
                        align="center"
                        sx={{
                          backgroundColor: "var(--warning-light, #fff3e0)",
                          position: "sticky",
                          top: 40,
                          zIndex: 3,
                          fontWeight: 500,
                          width: 70,
                          fontSize: "0.75rem",
                          py: 1,
                        }}
                      >
                        Rec
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          backgroundColor: "var(--warning-light, #fff3e0)",
                          position: "sticky",
                          top: 40,
                          zIndex: 3,
                          fontWeight: 500,
                          width: 70,
                          fontSize: "0.75rem",
                          py: 1,
                        }}
                      >
                        Days
                      </TableCell>

                      {/* Lateness */}
                      <TableCell
                        align="center"
                        sx={{
                          backgroundColor: "#f3e5f5",
                          position: "sticky",
                          top: 40,
                          zIndex: 3,
                          fontWeight: 500,
                          width: 70,
                          fontSize: "0.75rem",
                          py: 1,
                        }}
                      >
                        Rec
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          backgroundColor: "#f3e5f5",
                          position: "sticky",
                          top: 40,
                          zIndex: 3,
                          fontWeight: 500,
                          width: 70,
                          fontSize: "0.75rem",
                          py: 1,
                        }}
                      >
                        Mins
                      </TableCell>

                      {/* Personal */}
                      <TableCell
                        align="center"
                        sx={{
                          backgroundColor: "#e8f5e9",
                          position: "sticky",
                          top: 40,
                          zIndex: 3,
                          fontWeight: 500,
                          width: 70,
                          fontSize: "0.75rem",
                          py: 1,
                        }}
                      >
                        Rec
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          backgroundColor: "#e8f5e9",
                          position: "sticky",
                          top: 40,
                          zIndex: 3,
                          fontWeight: 500,
                          width: 70,
                          fontSize: "0.75rem",
                          py: 1,
                        }}
                      >
                        Hours
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {filteredTableData.map((employee) => {
                      // EXACT conversions: lateness in mins, personal in hours
                      const latenessMins = hoursToMinutes(
                        employee.totalLatenessHours,
                      );
                      const personalHours = employee.totalPersonalHours || 0; // Personal in HOURS
                      const sicknessDays = hoursToDays(
                        employee.totalSicknessHours,
                      );

                      return (
                        <TableRow
                          key={employee.employeeId}
                          hover
                          sx={{
                            "&:hover td:first-of-type": {
                              bgcolor: "action.hover",
                              position: "sticky",
                              left: 0,
                              zIndex: 2,
                            },
                            "& td": {
                              fontSize: "0.8125rem",
                              py: 1,
                            },
                          }}
                        >
                          <TableCell
                            sx={{
                              position: "sticky",
                              left: 0,
                              bgcolor: "background.paper",
                              zIndex: 1,
                              boxShadow: "2px 0 4px rgba(0,0,0,0.05)",
                            }}
                          >
                            <Box>
                              <Typography
                                variant="subtitle2"
                                fontWeight="600"
                                sx={{ fontSize: "0.875rem" }}
                              >
                                {employee.employeeName || "N/A"}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {employee.department || "Unknown"}
                              </Typography>
                            </Box>
                          </TableCell>

                          {/* Annual Leave */}
                          <TableCell
                            align="center"
                            sx={{ backgroundColor: "#f7fbff" }}
                          >
                            {employee.leaveDays?.ANNUAL_LEAVE !== undefined
                              ? employee.leaveDays.ANNUAL_LEAVE
                              : 0}
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ backgroundColor: "#f7fbff" }}
                          >
                            {employee.leaveDays?.UNPAID_LEAVE !== undefined
                              ? employee.leaveDays.UNPAID_LEAVE
                              : 0}
                          </TableCell>

                          {/* Sickness */}
                          <TableCell
                            align="center"
                            sx={{
                              backgroundColor:
                                "var(--warning-lighter, #fffaf3)",
                            }}
                          >
                            {employee.totalSicknessRecords || 0}
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              backgroundColor:
                                "var(--warning-lighter, #fffaf3)",
                            }}
                          >
                            {sicknessDays.toFixed(1)}
                          </TableCell>

                          {/* Lateness - EXACT MINUTES */}
                          <TableCell
                            align="center"
                            sx={{ backgroundColor: "#faf5fb" }}
                          >
                            {employee.totalLatenessRecords || 0}
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ backgroundColor: "#faf5fb" }}
                          >
                            {latenessMins > 0 ? `${latenessMins}m` : "0m"}
                          </TableCell>

                          {/* Personal - HOURS (1 decimal) */}
                          <TableCell
                            align="center"
                            sx={{ backgroundColor: "#f5fbf6" }}
                          >
                            {employee.totalPersonalRecords || 0}
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ backgroundColor: "#f5fbf6" }}
                          >
                            {Number(personalHours).toFixed(1)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Report Period Footer */}
            {filteredTableData.length > 0 && (
              <Box
                sx={{
                  mt: 1.5,
                  pt: 1.5,
                  borderTop: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography variant="caption" color="textSecondary">
                  Report Period:{" "}
                  {dateRange.from
                    ? new Date(dateRange.from).toLocaleDateString("en-GB")
                    : "All Time"}
                  {dateRange.to &&
                    ` to ${new Date(dateRange.to).toLocaleDateString("en-GB")}`}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    </LocalizationProvider>
  );
};

export default EmployeesAttendance;
